import React, { Fragment, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CheckCircleIcon,
  Divider,
  Dropdown,
  DropdownItem,
  InfoIcon,
  XCircleIcon,
} from "staysure-component-library";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import BtnLoadingSvg from "@/components/atoms/btn-loading-svg/btn-loading-svg";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import SmallButtonWrapper from "@/components/atoms/button-wrapper/small-button-wrapper";
import TextSelectionButton from "@/components/atoms/text-selection-button/text-selection-button";
import SocialProofChip from "@/components/molecules/quote-section/quote-cover/social-proof-chip/social-proof-chip";
import withBrand from "@/components/with-brand";
import { OptimizelyExperimentsEnum } from "@/config/app-constant";
import selectedColorBrand from "@/config/tailwind-colors/brand-colors";
import {
  ANNUAL_MULTI_TRIP,
  APPLICATION,
  BRAND_STAYSURE,
  COMPREHENSIVE,
  PRODUCT,
  SINGLE_TRIP,
  VARIANT_FLOW,
} from "@/constants";
import useUpdateCancellationCover from "@/hooks/api/quote/use-update-cancellation-cover";
import { useQuoteDisabled } from "@/hooks/use-quote-disabled/use-quote-disabled";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import {
  BenefitCopyDataType,
  CancellationCovers,
} from "@/schema/quote-page/page.schema";
import { RootState } from "@/store/root-reducer";
import { updateQuote } from "@/store/slices/quote/quote.slice";
import { DynamicCoverLimitProduct } from "@/types/DynamicCoverLimitProductType";
import headerClassNames from "@/utils/get-brand-quote-cover-top-classes.utils";
import { checkCurentLocation } from "@/utils/travel-date.utils";

import Styles from "./cover-level-selection-card-bottom.module.css";
import CoverDetailsDrawer from "../cover-details-drawer/cover-details-drawer";
import styles from "../cover-level-selection-card-top/cover-level-selection-card-top.module.css";

function ActiveIcon({ isActive = false }: Readonly<{ isActive?: boolean }>) {
  if (isActive) {
    return <CheckCircleIcon fill="#008205" stroke="#FFFFFF" />;
  }
  return (
    <XCircleIcon stroke={selectedColorBrand["text-disabled"]} strokeWidth={0} />
  );
}

export type SingleItem = {
  description: string;
  isActive: boolean;
  isCancelation?: boolean;
  isMedicalExpenses?: boolean;
  isExcludeUK?: boolean;
};

export type DrawerDataItem = {
  benefitCopy: string;
  benefitSubtitle: string;
  benefitTitle: string;
  id: string;
  isExcludeUK: boolean;
};

export type DrawerData = {
  drawerCopy: string;
  drawerDetails?: DrawerDataItem[];
  drawerTitle: string;
  closeDrawerCta?: string;
  isExcludeUK: boolean;
};

type CoverLevelSelectionCardBottomProps = {
  closeDrawerCta?: string;
  changeBtnLabel?: string;
  drawerData?: DrawerData;
  dynamicCoverLimit: DynamicCoverLimitProduct | null;
  id?: string | number;
  policyPackage: string;
  seeMoreDetailsBtnLabel?: string;
  tableDta?: BenefitCopyDataType[];
  keyValue?: string;
  setCancellationCoverOpen?: (val: boolean) => void;
  setCancelltionChangeCoverType?: (val: string) => void;
  setDynamicCoverLimit?: (val: CancellationCovers[] | null) => void;
  brand?: string;
  selectBtnInactiveLable?: string;
  selectBtnActiveLable?: string;
  isLoading?: boolean;
  selectState?: boolean;
  onSelect?: (val: string, state: boolean, index: number) => void;
  referenceId?: string;
  isSocialProofChipActive?: boolean;
  chipBottomText?: string;
  cardTitle?: string;
};

export const UK_REGION_COUNTRY_CODES = ["GB", "IM", "GG", "JE"];
export const UK_REGION_FROM_LOCATIONS = ["UK1", "UK3", "UK4", "UK5"];

function CoverLevelSelectionCardBottom({
  changeBtnLabel,
  drawerData = {} as DrawerData,
  dynamicCoverLimit,
  id = "",
  policyPackage,
  seeMoreDetailsBtnLabel,
  tableDta,
  keyValue,
  setCancellationCoverOpen,
  setCancelltionChangeCoverType,
  setDynamicCoverLimit,
  brand,
  selectBtnActiveLable,
  selectBtnInactiveLable,
  isLoading,
  selectState = false,
  onSelect,
  referenceId,
  isSocialProofChipActive = false,
  chipBottomText,
  cardTitle,
}: Readonly<CoverLevelSelectionCardBottomProps>) {
  const { drawerTitle, drawerCopy, drawerDetails, closeDrawerCta } = drawerData;
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch();
  const quoteData = useSelector((state: RootState) => state.quote);
  const { attributes } = quoteData;
  const { tripDetails } = attributes;
  const activePolicyPackage = tripDetails?.policyPackage;
  const { t } = useWizardTranslation();
  const isAbVariant = useMemo(() => {
    return (
      typeof window !== "undefined" &&
      localStorage.getItem(OptimizelyExperimentsEnum.RENEWAL_COVER_LEVEL) &&
      localStorage.getItem(OptimizelyExperimentsEnum.RENEWAL_COVER_LEVEL) ===
        VARIANT_FLOW &&
      APPLICATION === "renewal" &&
      PRODUCT === "staysure"
    );
  }, []);

  const isAbVariantChipPosition = useMemo(() => {
    if (
      typeof window !== "undefined" &&
      localStorage.getItem(
        OptimizelyExperimentsEnum.RENEWAL_COVER_LEVEL_CHIP
      ) &&
      APPLICATION === "renewal" &&
      PRODUCT === "staysure" &&
      isSocialProofChipActive &&
      chipBottomText
    ) {
      return localStorage.getItem(
        OptimizelyExperimentsEnum.RENEWAL_COVER_LEVEL_CHIP
      );
    }
    return false;
  }, [chipBottomText, isSocialProofChipActive]);

  const isCancellationDropdownVariant = useMemo(() => {
    return (
      typeof window !== "undefined" &&
      localStorage.getItem(
        OptimizelyExperimentsEnum.CANCELLATION_COVER_DROPDOWN
      ) === VARIANT_FLOW &&
      PRODUCT === BRAND_STAYSURE
    );
  }, []);

  const { current } = useWizard();
  const isShowCancellationOnTop =
    current?.uiElementControls?.isShowCancellationOnTop;

  const isQuoteDisabled = useQuoteDisabled();
  const isSelected = dynamicCoverLimit?.values.find(
    (value) => value.selected === true
  );

  const renderCancellationText = (data: BenefitCopyDataType) => {
    const { description, isCancelation } = data;
    if (isCancelation) {
      if (policyPackage === COMPREHENSIVE && !(brand === BRAND_STAYSURE)) {
        return `${
          (current.brandSpecificParams?.currencySymbol as string) ?? ""
        }5,000 ${description}`;
      }

      // For dropdown variant, show only description without numeric values
      if (isCancellationDropdownVariant) {
        return description;
      }

      if (isSelected) {
        return `${isSelected.displayValue} ${description}`;
      }
      return dynamicCoverLimit?.values[0]
        ? `${dynamicCoverLimit?.values[0].displayValue} ${description}`
        : description;
    }
    return description;
  };

  const openModal = () => {
    if (setCancellationCoverOpen) setCancellationCoverOpen(true);
    if (setDynamicCoverLimit)
      setDynamicCoverLimit(dynamicCoverLimit?.values ?? null);
    if (setCancelltionChangeCoverType)
      setCancelltionChangeCoverType(policyPackage);
  };

  // Hook for updating cancellation cover via API
  const resetTrigger = useCallback(() => {
    // Reset trigger for cleanup if needed
  }, []);

  const {
    data: updateData,
    loading: updateLoading,
    error: updateError,
    updateCancellationCover,
  } = useUpdateCancellationCover({ resetTrigger });

  // Handle API response for dropdown changes
  React.useEffect(() => {
    if (!updateLoading && updateData && !updateError) {
      dispatch(updateQuote(updateData));
    }
  }, [updateData, updateLoading, updateError, dispatch]);

  // Handle dropdown change for cancellation cover
  const handleDropdownChange = useCallback(
    (selectedValue: string | React.Key | null) => {
      if (!selectedValue || !dynamicCoverLimit?.values) return;

      const selectedCancellationCover = dynamicCoverLimit.values.map(
        (cover) => ({
          selected: cover.value.toString() === selectedValue.toString(),
          value: cover.value,
        })
      );

      const requestdata = {
        policyType: tripDetails?.policyType,
        policyPackage: policyPackage.toUpperCase(),
        selectedCancellationCover,
      };

      const bodyData = {
        dynamicCoverLimit: [
          {
            code: "CANCELLATION",
            product: [
              {
                product: "CORE",
                policyType: requestdata.policyType,
                policyPackage: requestdata.policyPackage,
                values: requestdata.selectedCancellationCover,
              },
            ],
          },
        ],
      };

      const tripDetailsBody = {
        ...attributes.tripDetails,
        policyPackage: activePolicyPackage?.toUpperCase(),
        policyType: requestdata.policyType,
      };

      updateCancellationCover(bodyData, tripDetailsBody);
    },
    [
      dynamicCoverLimit,
      tripDetails,
      policyPackage,
      attributes,
      updateCancellationCover,
    ]
  );

  const isUKRegionFromLocation = useMemo(() => {
    return (
      tripDetails?.fromLocation &&
      UK_REGION_FROM_LOCATIONS.includes(tripDetails.fromLocation)
    );
  }, [tripDetails]);

  const isSameCountry = checkCurentLocation(
    tripDetails?.fromLocation ?? "",
    (tripDetails?.toLocation || tripDetails?.area) ?? ""
  );

  const isUKRegionToLocationsOnly = useMemo(() => {
    const toLocationArr = tripDetails?.toLocation?.split(",") ?? [];
    return toLocationArr.every((substring) =>
      UK_REGION_COUNTRY_CODES.includes(substring)
    );
  }, [tripDetails]);

  const isActive = (data: BenefitCopyDataType) => {
    if (data.isMedicalExpenses && isSameCountry) {
      return false;
    }

    if (
      data.isMedicalExpenses &&
      isUKRegionFromLocation &&
      ((tripDetails?.area === "UK" &&
        tripDetails?.policyType === ANNUAL_MULTI_TRIP) ||
        (tripDetails?.policyType === SINGLE_TRIP && isUKRegionToLocationsOnly))
    ) {
      return false;
    }

    return data.isActive;
  };

  const filteredDrawerDetails = useMemo(() => {
    return drawerDetails?.filter((detail) => {
      const excludeUKCountry =
        detail.isExcludeUK &&
        isUKRegionFromLocation &&
        ((tripDetails?.area === "UK" &&
          tripDetails?.policyType === ANNUAL_MULTI_TRIP) ||
          (tripDetails?.policyType === SINGLE_TRIP &&
            isUKRegionToLocationsOnly));

      const excludeSameCountry = detail.isExcludeUK && isSameCountry;

      return !(excludeUKCountry || excludeSameCountry);
    });
  }, [
    drawerDetails,
    tripDetails,
    isUKRegionFromLocation,
    isUKRegionToLocationsOnly,
    isSameCountry,
  ]);

  const shouldExcludeUKCountries = useMemo(() => {
    return (
      (isUKRegionFromLocation &&
        ((tripDetails?.area === "UK" &&
          tripDetails?.policyType === ANNUAL_MULTI_TRIP) ||
          (tripDetails?.policyType === SINGLE_TRIP &&
            isUKRegionToLocationsOnly))) ||
      isSameCountry
    );
  }, [
    tripDetails,
    isUKRegionFromLocation,
    isUKRegionToLocationsOnly,
    isSameCountry,
  ]);

  const filteredTableData = useMemo(() => {
    const filtered = shouldExcludeUKCountries
      ? tableDta?.filter((data) => !data.isExcludeUK)
      : tableDta;

    // Move cancellation item to the top of the list only when conditions are met
    if (filtered && isCancellationDropdownVariant && isShowCancellationOnTop) {
      const cancellationIndex = filtered.findIndex(
        (data) => data.isCancelation
      );
      if (cancellationIndex > 0) {
        const reordered = [...filtered];
        const [cancellationItem] = reordered.splice(cancellationIndex, 1);
        reordered.unshift(cancellationItem);
        return reordered;
      }
    }

    return filtered;
  }, [
    shouldExcludeUKCountries,
    tableDta,
    isCancellationDropdownVariant,
    isShowCancellationOnTop,
  ]);

  return (
    <div data-testid="CoveringCard-bottom-testid">
      {filteredTableData?.map((data, index) => {
        if (index === 0 && APPLICATION === "renewal") {
          return (
            <Fragment key={index as unknown as string}>
              <div
                data-ab-id="cover-level-info-line"
                className={`flex mb-spacing-s ${
                  index === 0 ? "" : "mt-spacing-s"
                } items-start justify-center ${Styles.xCircleSvg}`}
              >
                <div
                  className={`${
                    Styles.iconInfo
                  } ${keyValue?.toLocaleLowerCase()}`}
                >
                  <InfoIcon stroke="#ffffff" />
                </div>
                <div>
                  <p className="ml-spacing-xxs text-[16px] leading-[24px] font-body-font font-[400] text-text-functional">
                    {data.description}
                  </p>
                </div>
              </div>
              {index !== filteredTableData.length - 1 && (
                <Divider thickness={1} color="#DCDCDC" />
              )}
            </Fragment>
          );
        }

        if (data.isMedicalExpenses && !isActive(data)) {
          return null;
        }

        const shouldRenderChangeButton =
          data?.isCancelation &&
          dynamicCoverLimit?.values &&
          dynamicCoverLimit?.values.length > 1 &&
          (brand === BRAND_STAYSURE || policyPackage !== COMPREHENSIVE);
        return (
          <Fragment key={index as unknown as string}>
            <div
              className={`flex flex-col md:flex-row mb-spacing-s ${
                index === 0 ? "" : "mt-spacing-s"
              } md:items-center md:justify-between ${Styles.xCircleSvg}`}
            >
              <div className="flex items-center mb-[4px] md:mb-o">
                <div>
                  <ActiveIcon isActive={isActive(data)} />
                </div>
                <div>
                  <p className="ml-spacing-xxs text-[16px] leading-[24px] font-body-font font-[400] text-text-functional">
                    {renderCancellationText(data)}
                  </p>
                </div>
              </div>
              {shouldRenderChangeButton && (
                <div className="w-full md:w-[121px] lg:w-auto md:ml-auto">
                  {isCancellationDropdownVariant ? (
                    <div
                      className={`${Styles.dropdownWrapper} relative flex items-center w-full h-[40px] md:w-[121px] md:justify-center lg:w-auto md:h-[40px]`}
                    >
                      <Dropdown
                        key={`${
                          isSelected?.displayValue ??
                          dynamicCoverLimit?.values[0]?.displayValue
                        }`}
                        id={`cancellation-cover-dropdown-${policyPackage}`}
                        className={`${Styles.golfDropdownInactive}  w-full  md:w-fit md:min-w-[121px] pl-[16px] pr-[12px] py-[8px] h-[40px] rounded-radius-full border-2 border-interactive-secondary-default !text-interactive-secondary-default !text-[18px] font-body-font leading-[100%] font-[600] !bg-surface-neutral-option-1 disabled:cursor-not-allowed disabled:border-border-disabled items-center`}
                        listClassName="!items-center w-full !min-w-[248px] md:w-fit md:!min-w-[97px] mb-[4px] !p-[4px]] !bg-transparent min-h-[43px] !text-[18px] !leading-[27px] !text-text-functional
                    rounded-radius-s hover:!bg-interactive-action-hovered focus:!bg-interactive-action-hovered"
                        unorderedListClassName={`w-full min-w-[280px] md:w-fit md:!min-w-[121px] !py-[4px] !px-[12px] !mt-[0px] border !rounded-radius-xxl !border-border-subdued !bg-surface-neutral-option-1 !shadow-[0px_0px_12px_0px_rgba(15,23,42,0.11)]`}
                        onSelectionChange={handleDropdownChange}
                        defaultValue={`${
                          isSelected?.value ??
                          dynamicCoverLimit?.values[0]?.value ??
                          ""
                        }`}
                        isDisabled={isQuoteDisabled}
                      >
                        {dynamicCoverLimit?.values.map((cover, coverIndex) => (
                          <DropdownItem
                            value={`${cover.value}`}
                            key={`dropdown-item-${coverIndex}`}
                            keyValue={`${cover.value}`}
                          >
                            <span className="w-full items-center leading-[18px] text-[18px] font-semibold  hover:bg-interactive-action-hovered focus:bg-interactive-action-hovered">
                              {cover.displayValue}
                            </span>
                          </DropdownItem>
                        ))}
                      </Dropdown>
                    </div>
                  ) : (
                    <TextSelectionButton
                      id={`cover-level-selection-card-bottom-${index}-${id}`}
                      label={changeBtnLabel ?? "Change"}
                      className={` text-[16px] font-body-font font-[600] align-top leading-[150%]
        hover:text-interactive-secondary-default hover:bg-surface-neutral-option-1 ml-[4px] !px-0`}
                      onClick={openModal}
                      isDisabled={isQuoteDisabled}
                    />
                  )}
                </div>
              )}
            </div>
            {index !== filteredTableData.length - 1 && (
              <Divider thickness={1} color="#DCDCDC" />
            )}
          </Fragment>
        );
      })}

      <div data-ab-id="cover-level-see-more" className="px-[3px]">
        <LabelButtonWrapper
          data-testid={`cover-level-selection-card-${id}-see-more-details-button`}
          id={`cover-level-selection-card-${id}-see-more-details-button`}
          hierarchy="tertiary"
          label={seeMoreDetailsBtnLabel ?? "See more details"}
          className="text-[16px] font-body-font font-[600]
          !px-0 leading-[19.49px] h-[17px]"
          onClick={() => setIsOpen(true)}
          isDisabled={isQuoteDisabled}
        />
      </div>

      {isAbVariant && (
        <div
          className="flex items-center h-auto px-6"
          data-testid="select-btn-wrapper-testid"
        >
          <SmallButtonWrapper
            id={`cover-level-selection-${id}-card-select-button`}
            data-testid={`cover-level-selection-${id}-card-select-button`}
            activeLabel={selectBtnActiveLable ?? ""}
            inactiveLabel={selectBtnInactiveLable ?? ""}
            hierarchy="primary"
            isRightIcon
            className={`${styles.selectBtn} ${
              !selectState ? "blockClass" : ""
            } max-h-[48px] h-[48px] w-full !text-[18px] leading-[100%] font-[600] font-body-font p-spacing-s disabled:cursor-not-allowed`}
            onClick={(state) =>
              onSelect &&
              onSelect(
                keyValue as keyof typeof headerClassNames,
                state,
                id as number
              )
            }
            state={selectState}
            isLoading={isLoading}
            loadingIcon={<BtnLoadingSvg />}
            isClickableDisabled={
              referenceId === "" || referenceId === undefined
            }
            isDisabled={isQuoteDisabled}
          />
        </div>
      )}
      {isAbVariantChipPosition &&
        isAbVariantChipPosition === "bottom" &&
        chipBottomText && (
          <SocialProofChip
            chipLabel={chipBottomText}
            position="bottom"
            cardTitle={cardTitle ?? ""}
          />
        )}

      <CoverDetailsDrawer
        closeDrawerCta={closeDrawerCta ?? t("close", "Close")}
        coverLevelTitle={drawerTitle}
        isOpen={isOpen}
        setOpen={(e) => setIsOpen(e)}
        drawerCopy={drawerCopy}
        drawerData={filteredDrawerDetails}
      />
    </div>
  );
}

export default withBrand(CoverLevelSelectionCardBottom);
